import React, { useEffect, useState } from 'react'

import { Container } from 'react-bootstrap'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'

import reviewAxiosClient from './reviewAxiosClient'
import PublicLayout from '../common/PublicLayout'
import PostDTO from '../types/Post'
import { ReviewComment } from '../types/Review'
import SocialNetwork from '../types/SocialNetwork'

interface PostWithComments extends PostDTO {
    comments: ReviewComment[]
}

function ReviewPostsPage() {
    const [posts, setPosts] = useState<PostWithComments[]>([])
    const [loading, setLoading] = useState(true)
    const [approvals, setApprovals] = useState<Record<number, boolean>>({})
    const [notes, setNotes] = useState<Record<number, string>>({})
    const [submittingAll, setSubmittingAll] = useState(false)
    const [expandedComments, setExpandedComments] = useState<Record<number, boolean>>({})
    const navigate = useNavigate()
    const { token } = useParams<{ token: string }>()
    const clientName = localStorage.getItem('reviewClientName') || ''

    useEffect(() => {
        const reviewToken = localStorage.getItem('reviewAccessToken')
        if (!reviewToken) {
            navigate(`/review/${token}`, { replace: true })
            return
        }
        loadPosts()
    }, [])

    const loadPosts = async () => {
        try {
            const response = await reviewAxiosClient.get('review/posts')
            const postsData = response.data as PostDTO[]

            const postsWithComments: PostWithComments[] = await Promise.all(
                postsData.map(async (post) => {
                    try {
                        const commentsResponse = await reviewAxiosClient.get(`review/posts/${post.id}/comments`)
                        return { ...post, comments: commentsResponse.data }
                    } catch {
                        return { ...post, comments: [] }
                    }
                })
            )

            setPosts(postsWithComments)
        } catch (error: any) {
            if (error?.response?.status === 401) {
                navigate(`/review/${token}`, { replace: true })
            }
            console.error('Error loading posts:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleApproveAll = () => {
        const allApprovals: Record<number, boolean> = {}
        posts.forEach(post => {
            if (post.id != null) allApprovals[post.id] = true
        })
        setApprovals(allApprovals)
    }

    const handleSubmitAll = async () => {
        const toSubmit = posts.filter(post => {
            if (post.id == null) return false
            return approvals[post.id] || (notes[post.id] && notes[post.id].trim() !== '')
        })

        if (toSubmit.length === 0) {
            toast.error('Ziadne prispevky na odoslanie. Zaskrtnite schvalenie alebo pridajte poznamku.')
            return
        }

        setSubmittingAll(true)
        let successCount = 0
        let errorCount = 0

        for (const post of toSubmit) {
            const reviewAction = approvals[post.id!] ? 'APPROVED' : 'CHANGE_REQUIRED'
            try {
                await reviewAxiosClient.post(`review/posts/${post.id}/comment`, {
                    comment: notes[post.id!] || '',
                    reviewAction,
                })
                successCount++
            } catch (error) {
                console.error(`Error submitting post ${post.id}:`, error)
                errorCount++
            }
        }

        if (successCount > 0) {
            toast.success(`Uspesne odoslane: ${successCount} prispevkov`)
        }
        if (errorCount > 0) {
            toast.error(`Chyba pri odosielani: ${errorCount} prispevkov`)
        }

        setApprovals({})
        setNotes({})
        setSubmittingAll(false)
        await loadPosts()
    }

    const getSocialNetworkIcon = (network: SocialNetwork) => {
        switch (network) {
            case SocialNetwork.FACEBOOK:
                return 'bi-facebook'
            case SocialNetwork.INSTAGRAM:
                return 'bi-instagram'
            case SocialNetwork.LINKED_IN:
                return 'bi-linkedin'
            default:
                return ''
        }
    }

    const getSocialNetworkLabel = (network: SocialNetwork) => {
        switch (network) {
            case SocialNetwork.FACEBOOK:
                return 'Facebook'
            case SocialNetwork.INSTAGRAM:
                return 'Instagram'
            case SocialNetwork.LINKED_IN:
                return 'LinkedIn'
            default:
                return network
        }
    }

    const getApprovalBadge = (status: string | null) => {
        if (!status) return null
        switch (status) {
            case 'APPROVED':
                return <span className="badge bg-success">Schvalene</span>
            case 'CHANGE_REQUIRED':
                return <span className="badge bg-warning text-dark">Pozadovana zmena</span>
            case 'DECLINED':
                return <span className="badge bg-danger">Zamietnute</span>
            default:
                return <span className="badge bg-secondary">{status}</span>
        }
    }

    const getPhotoUrl = (photo: string) => {
        if (photo.startsWith('http')) return photo
        return `${process.env.REACT_APP_SERVER_URL}/storage/${photo}`
    }

    const truncateText = (text: string, maxLength: number) => {
        if (text.length <= maxLength) return text
        return text.substring(0, maxLength) + '...'
    }

    const toggleComments = (postId: number) => {
        setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }))
    }

    const getReviewActionBadge = (action: string) => {
        switch (action) {
            case 'APPROVED':
                return <span className="badge bg-success me-1">Schvalene</span>
            case 'CHANGE_REQUIRED':
                return <span className="badge bg-warning text-dark me-1">Pozadovana zmena</span>
            default:
                return null
        }
    }

    if (loading) {
        return (
            <PublicLayout>
                <Container>
                    <div className="text-center py-5">Nacitavam...</div>
                </Container>
            </PublicLayout>
        )
    }

    return (
        <PublicLayout>
            <Container fluid>
                <div className="py-4">
                    <h2 className="text-center mb-2">Schvalovanie prispevkov</h2>
                    {clientName && <p className="text-center text-muted mb-4">{clientName}</p>}

                    {posts.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted">Ziadne prispevky na schvalenie</p>
                        </div>
                    ) : (
                        <>
                            <div className="d-flex justify-content-end gap-2 mb-3">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handleApproveAll}
                                    disabled={submittingAll}
                                >
                                    Hromadne schvalit
                                </button>
                                <button
                                    className="amani-primary"
                                    onClick={handleSubmitAll}
                                    disabled={submittingAll}
                                >
                                    {submittingAll ? 'Odosielam...' : 'Odoslat'}
                                </button>
                            </div>

                            <div className="rounded-content-body" style={{ overflowX: 'auto' }}>
                                <table className="table table-hover mb-0" style={{ minWidth: '1000px' }}>
                                    <thead>
                                        <tr>
                                            <th>Klient</th>
                                            <th>Socialna siet</th>
                                            <th>Datum publikovania</th>
                                            <th>Typ prispevku</th>
                                            <th>Obrazok/video</th>
                                            <th style={{ maxWidth: '200px' }}>Text prispevku</th>
                                            <th className="text-center">Schvalene</th>
                                            <th>Publikovane</th>
                                            <th style={{ minWidth: '180px' }}>Poznamky</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {posts.map(post => (
                                            <React.Fragment key={post.id}>
                                                <tr>
                                                    <td className="align-middle">{clientName}</td>
                                                    <td className="align-middle">
                                                        <i className={`bi ${getSocialNetworkIcon(post.socialNetwork)} me-1`}></i>
                                                        {getSocialNetworkLabel(post.socialNetwork)}
                                                    </td>
                                                    <td className="align-middle">
                                                        {post.publishFrom
                                                            ? new Date(post.publishFrom).toLocaleDateString('sk-SK')
                                                            : '-'}
                                                    </td>
                                                    <td className="align-middle">{post.postType || '-'}</td>
                                                    <td className="align-middle">
                                                        {post.photo ? (
                                                            <div className="d-flex gap-1">
                                                                {post.photo.split(',').slice(0, 3).map((p: string, i: number) => (
                                                                    <img
                                                                        key={i}
                                                                        src={getPhotoUrl(p)}
                                                                        alt={post.title}
                                                                        style={{
                                                                            width: '60px',
                                                                            height: '60px',
                                                                            objectFit: 'cover',
                                                                            borderRadius: '4px',
                                                                        }}
                                                                    />
                                                                ))}
                                                                {post.photo.split(',').length > 3 && (
                                                                    <span className="align-self-center text-muted" style={{fontSize: '12px'}}>
                                                                        +{post.photo.split(',').length - 3}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="align-middle" style={{ maxWidth: '200px' }}>
                                                        {post.description
                                                            ? truncateText(post.description, 100)
                                                            : '-'}
                                                    </td>
                                                    <td className="align-middle text-center">
                                                        <div className="d-flex flex-column align-items-center gap-2">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                style={{ width: '20px', height: '20px', borderRadius: '4px', cursor: 'pointer', margin: 0 }}
                                                                checked={approvals[post.id!] || false}
                                                                onChange={(e) =>
                                                                    setApprovals(prev => ({
                                                                        ...prev,
                                                                        [post.id!]: e.target.checked,
                                                                    }))
                                                                }
                                                                disabled={submittingAll}
                                                            />
                                                            {getApprovalBadge(post.postApprovalStatus)}
                                                        </div>
                                                    </td>
                                                    <td className="align-middle">
                                                        {post.postStatus === 'PUBLISHED' && <span className="badge bg-success">Publikovane</span>}
                                                        {post.postStatus === 'SCHEDULED' && <span className="badge bg-info text-dark">Naplanovany</span>}
                                                        {post.postStatus === 'NOT_PUBLISHED' && <span className="badge bg-danger">Nepublikovane</span>}
                                                        {!post.postStatus && <span className="badge bg-secondary">-</span>}
                                                    </td>
                                                    <td className="align-middle">
                                                        <textarea
                                                            className="form-control form-control-sm"
                                                            rows={2}
                                                            placeholder="Poznamka..."
                                                            value={notes[post.id!] || ''}
                                                            onChange={(e) =>
                                                                setNotes(prev => ({
                                                                    ...prev,
                                                                    [post.id!]: e.target.value,
                                                                }))
                                                            }
                                                            disabled={submittingAll}
                                                        />
                                                    </td>
                                                    <td className="align-middle">
                                                        {post.comments.length > 0 && (
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={() => toggleComments(post.id!)}
                                                                title="Zobrazit komentare"
                                                            >
                                                                <i className={`bi ${expandedComments[post.id!] ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                                                <span className="ms-1">{post.comments.length}</span>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                                {expandedComments[post.id!] && post.comments.length > 0 && (
                                                    <tr>
                                                        <td colSpan={10} style={{ backgroundColor: '#f8f9fa' }}>
                                                            <div className="p-2">
                                                                <strong className="d-block mb-2">Predchadzajuce komentare:</strong>
                                                                {post.comments.map(comment => (
                                                                    <div key={comment.id} className="mb-2 p-2" style={{ backgroundColor: '#fff', borderRadius: '4px' }}>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            {getReviewActionBadge(comment.reviewAction)}
                                                                            <small className="text-muted">
                                                                                {new Date(comment.createdAt).toLocaleDateString('sk-SK')}{' '}
                                                                                {new Date(comment.createdAt).toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' })}
                                                                            </small>
                                                                        </div>
                                                                        {comment.comment && <p className="mb-0 mt-1">{comment.comment}</p>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handleApproveAll}
                                    disabled={submittingAll}
                                >
                                    Hromadne schvalit
                                </button>
                                <button
                                    className="amani-primary"
                                    onClick={handleSubmitAll}
                                    disabled={submittingAll}
                                >
                                    {submittingAll ? 'Odosielam...' : 'Odoslat'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </Container>
        </PublicLayout>
    )
}

export default ReviewPostsPage
